import { useCallback, useRef, useState } from 'react';
import { Download, FileDown, RefreshCw, Share2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { AppButton, AppText } from '@/components/common';
import { AppLoader } from '@/components/feedback';
import { AppModal } from '@/components/feedback/AppModal';
import appLogo from '@/assets/icons/applogo.png';
import type { BookReceipt } from '@/types/media';
import type { GivingReceipt } from '@/types/giving';

const formatMoney = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
};

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || 'Not provided';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatStatus = (status: string) =>
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export interface ReceiptModalProps {
  open: boolean;
  onClose: () => void;
  receipt?: GivingReceipt | null;
  bookReceipt?: BookReceipt | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const ReceiptModal = ({ open, onClose, receipt, bookReceipt, loading, error, onRetry }: ReceiptModalProps) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Support both GivingReceipt and BookReceipt
  const receiptNumber = bookReceipt?.receiptNumber ?? receipt?.receiptNumber ?? '';
  const isBookReceipt = Boolean(bookReceipt);

  const handleDownloadImage = useCallback(async () => {
    if (!receiptRef.current || loading || error || isDownloading) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const dataUrl = await toPng(receiptRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      if (!dataUrl) throw new Error('Failed to generate image');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `receipt-${receiptNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setDownloadError('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [receiptNumber, loading, error, isDownloading]);

  const handleDownloadPdf = useCallback(async () => {
    if (!receiptRef.current || loading || error || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    setDownloadError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const dataUrl = await toPng(receiptRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      if (!dataUrl) throw new Error('Failed to generate image');
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = dataUrl;
      });
      if (img.width === 0 || img.height === 0) throw new Error('Invalid image dimensions');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const scale = Math.min((210 - margin * 2) / img.width, (297 - margin * 2) / img.height);
      const pdfWidth = img.width * scale;
      const pdfHeight = img.height * scale;
      const x = (210 - pdfWidth) / 2;
      const y = (297 - pdfHeight) / 2;
      pdf.addImage(dataUrl, 'PNG', x, y, pdfWidth, pdfHeight);
      pdf.save(`receipt-${receiptNumber}.pdf`);
    } catch {
      setDownloadError('PDF download failed. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [receiptNumber, loading, error, isDownloadingPdf]);

  const handleShare = useCallback(async () => {
    if (!receiptRef.current || loading || error) return;
    try {
      const dataUrl = await toPng(receiptRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `receipt-${receiptNumber}.png`, { type: 'image/png' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: 'Receipt', files: [file] });
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `receipt-${receiptNumber}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
    }
  }, [receiptNumber, loading, error]);

  const renderGivingReceiptContent = () => {
    if (!receipt) return null;

    const domainLabel = receipt.domain ? formatStatus(receipt.domain) : 'Giving';

    return (
      <div ref={receiptRef} className="rounded-xl border border-[#E5E7EB] bg-white p-5">
        <div className="flex flex-col items-center gap-2 pb-4 text-center">
          <img src={appLogo} alt="BaptistOne" className="size-12 rounded-xl object-contain" />
          <AppText variant="bodyMedium" weight="bold" color="#0B1F4A">
            BaptistOne {domainLabel} Receipt
          </AppText>
          <AppText variant="caption" color="textMuted" weight="bold">
            {receipt.receiptNumber}
          </AppText>
        </div>

        <div className="border-t border-dashed border-[#D1D5DB] pt-4">
          <div className="grid gap-3">
            <div className="flex items-center justify-between gap-3">
              <AppText variant="caption" color="textMuted" weight="bold">
                Description
              </AppText>
              <AppText variant="bodySmall" weight="bold">
                {receipt.description}
              </AppText>
            </div>
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Amount
                </AppText>
                <AppText variant="bodyMedium" weight="bold">
                  {formatMoney(receipt.amount, receipt.currency)}
                </AppText>
              </div>
            </div>
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Date
                </AppText>
                <AppText variant="bodySmall" weight="bold">
                  {formatDate(receipt.createdAt)}
                </AppText>
              </div>
            </div>
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Reference
                </AppText>
                <AppText variant="caption" weight="bold" className="break-all text-right">
                  {receipt.transactionReference}
                </AppText>
              </div>
            </div>
            {receipt.from && (
              <div className="border-t border-dashed border-[#D1D5DB] pt-3">
                <div className="flex items-center justify-between gap-3">
                  <AppText variant="caption" color="textMuted" weight="bold">
                    From
                  </AppText>
                  <AppText variant="bodySmall" weight="bold">
                    {receipt.from.name}
                  </AppText>
                </div>
              </div>
            )}
            {receipt.to && (
              <div className="border-t border-dashed border-[#D1D5DB] pt-3">
                <div className="flex items-center justify-between gap-3">
                  <AppText variant="caption" color="textMuted" weight="bold">
                    To
                  </AppText>
                  <AppText variant="bodySmall" weight="bold">
                    {receipt.to.name}
                  </AppText>
                </div>
              </div>
            )}
            {receipt.finance?.note && (
              <div className="border-t border-dashed border-[#D1D5DB] pt-3">
                <div className="flex items-center justify-between gap-3">
                  <AppText variant="caption" color="textMuted" weight="bold">
                    Note
                  </AppText>
                  <AppText variant="bodySmall" weight="bold">
                    {receipt.finance.note}
                  </AppText>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBookReceiptContent = () => {
    if (!bookReceipt) return null;

    return (
      <div ref={receiptRef} className="rounded-xl border border-[#E5E7EB] bg-white p-5">
        <div className="flex flex-col items-center gap-2 pb-4 text-center">
          <img src={appLogo} alt="BaptistOne" className="size-12 rounded-xl object-contain" />
          <AppText variant="bodyMedium" weight="bold" color="#0B1F4A">
            BaptistOne Book Receipt
          </AppText>
          <AppText variant="caption" color="textMuted" weight="bold">
            {bookReceipt.receiptNumber}
          </AppText>
        </div>

        <div className="border-t border-dashed border-[#D1D5DB] pt-4">
          <div className="grid gap-3">
            {/* Status */}
            <div className="flex items-center justify-between gap-3">
              <AppText variant="caption" color="textMuted" weight="bold">
                Status
              </AppText>
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 uppercase">
                {formatStatus(bookReceipt.status)}
              </span>
            </div>

            {/* Items */}
            {bookReceipt.items.length > 0 && (
              <div className="border-t border-dashed border-[#D1D5DB] pt-3">
                <AppText variant="caption" color="textMuted" weight="bold" className="mb-2 block">
                  Items
                </AppText>
                <div className="grid gap-2">
                  {bookReceipt.items.map((item) => (
                    <div key={item.id} className="flex items-start justify-between gap-3 rounded-lg bg-[#F8FAFC] px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <AppText variant="bodySmall" weight="bold" className="line-clamp-1">
                          {item.titleSnapshot}
                        </AppText>
                        <AppText variant="caption" color="textMuted">
                          {item.formatType} · Qty {item.quantity} × {formatMoney(item.unitPrice, bookReceipt.currency)}
                        </AppText>
                      </div>
                      <AppText variant="bodySmall" weight="bold" color="#123B8D">
                        {formatMoney(item.lineTotal, bookReceipt.currency)}
                      </AppText>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subtotal */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Subtotal
                </AppText>
                <AppText variant="bodySmall" weight="bold">
                  {formatMoney(bookReceipt.subtotalAmount, bookReceipt.currency)}
                </AppText>
              </div>
            </div>

            {/* Delivery Fee */}
            {bookReceipt.deliveryFeeAmount > 0 && (
              <div className="border-t border-dashed border-[#D1D5DB] pt-3">
                <div className="flex items-center justify-between gap-3">
                  <AppText variant="caption" color="textMuted" weight="bold">
                    Delivery
                  </AppText>
                  <AppText variant="bodySmall" weight="bold">
                    {formatMoney(bookReceipt.deliveryFeeAmount, bookReceipt.currency)}
                  </AppText>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Total
                </AppText>
                <AppText variant="bodyMedium" weight="bold" color="#123B8D">
                  {formatMoney(bookReceipt.totalAmount, bookReceipt.currency)}
                </AppText>
              </div>
            </div>

            {/* Payment */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Payment
                </AppText>
                <AppText variant="bodySmall" weight="bold">
                  {formatStatus(bookReceipt.paymentMethod)}
                </AppText>
              </div>
            </div>

            {/* Date */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Date
                </AppText>
                <AppText variant="bodySmall" weight="bold">
                  {formatDate(bookReceipt.createdAt)}
                </AppText>
              </div>
            </div>

            {/* Reference */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Reference
                </AppText>
                <AppText variant="caption" weight="bold" className="break-all text-right">
                  {bookReceipt.transactionReference}
                </AppText>
              </div>
            </div>

            {/* Order ID */}
            <div className="border-t border-dashed border-[#D1D5DB] pt-3">
              <div className="flex items-center justify-between gap-3">
                <AppText variant="caption" color="textMuted" weight="bold">
                  Order ID
                </AppText>
                <AppText variant="caption" weight="bold" className="break-all font-mono text-right">
                  {bookReceipt.orderId}
                </AppText>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <section className="grid min-h-48 place-items-center">
          <AppLoader label="Loading receipt" />
        </section>
      );
    }

    if (error) {
      return (
        <section className="grid gap-3 place-items-center py-8">
          <AppText variant="bodyMedium" color="#B91C1C" align="center">
            {error}
          </AppText>
          <AppButton leftIcon={<RefreshCw className="size-4" aria-hidden />} variant="outline" onClick={onRetry}>
            Retry
          </AppButton>
        </section>
      );
    }

    if (isBookReceipt) return renderBookReceiptContent();
    return renderGivingReceiptContent();
  };

  const hasData = isBookReceipt ? Boolean(bookReceipt) : Boolean(receipt);

  return (
    <AppModal
      open={open}
      title="Receipt"
      onClose={onClose}
      footer={
        hasData && !loading && !error ? (
          <>
            <AppButton
              variant="secondary"
              leftIcon={<Share2 className="size-4" aria-hidden />}
              onClick={() => void handleShare()}
            >
              Share
            </AppButton>
            <AppButton
              leftIcon={<Download className="size-4" aria-hidden />}
              onClick={() => void handleDownloadImage()}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download Image'}
            </AppButton>
            <AppButton
              variant="outline"
              leftIcon={<FileDown className="size-4" aria-hidden />}
              onClick={() => void handleDownloadPdf()}
              disabled={isDownloadingPdf}
            >
              {isDownloadingPdf ? 'Generating...' : 'Download PDF'}
            </AppButton>
          </>
        ) : undefined
      }
    >
      {renderContent()}
    </AppModal>
  );
};
